import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import PropTypes from "prop-types";
import AOS from "aos";
import "aos/dist/aos.css";

import ResponsiveButton from "../../components/ResponsiveButton/ResponsiveButton";
import { MdArrowBack, MdArrowForward } from "react-icons/md";

import transportImage from "../../assets/perakarry.png";
import opastusImage from "../../assets/xlr.jpg";
import valoaaniImage from "../../assets/valojaani.jpg";
import artistTechImage from "../../assets/joyride_lava.webp";

/* =====================================================
   ANALYTICS – CTA EVENT
===================================================== */
const trackCTA = (eventName, label) => {
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("event", eventName, {
      event_category: "palvelut",
      event_label: label,
      sivu: "palvelut",
    });
  }
};

const serviceQuickLinks = [
  {
    label: "Tekniikka esiintyjille",
    href: "#tekniikka-esiintyjille",
  },
  {
    label: "Koko illan toteutus",
    href: "#valojaanen",
  },
  {
    label: "Asennus ja opastus",
    href: "#opastuspalvelu",
  },
  {
    label: "Kuljetus ja peräkärry",
    href: "#perakarryn-vuokraus",
  },
];

/* =====================================================
   YLEINEN PALVELUOSIO
===================================================== */
const ServiceSection = ({
  id,
  eyebrow,
  title,
  image,
  imageAlt,
  reverse = false,
  children,
  ctaLabel,
  ctaAction,
}) => {
  return (
    <section
      id={id}
      className="
        scroll-mt-32 overflow-hidden rounded-[2rem] border border-[#DDE7E5]
        bg-white/90 p-6 shadow-xl backdrop-blur-xl
        dark:border-darkborder dark:bg-[#111114]/95
        sm:p-8 lg:p-10
      "
      data-aos="fade-up"
    >
      <div
        className={`grid grid-cols-1 items-start gap-10 lg:grid-cols-2 ${
          reverse ? "lg:[&>*:first-child]:order-2" : ""
        }`}
      >
        {/* KUVA */}
        <div
          className="
            group relative overflow-hidden rounded-[1.5rem] border border-[#DDE7E5]
            bg-[#F8FAFC] shadow-lg
            dark:border-darkborder dark:bg-darksurface
          "
        >
          <div
            aria-hidden="true"
            className="
              pointer-events-none absolute inset-0 z-10
              bg-gradient-to-t from-black/35 via-transparent to-transparent
              opacity-70
            "
          />

          <img
            src={image}
            alt={imageAlt}
            loading="lazy"
            className="
              h-full min-h-[260px] w-full object-cover
              transition duration-500 group-hover:scale-[1.03]
              sm:min-h-[320px] lg:min-h-[420px]
            "
          />
        </div>

        {/* TEKSTI + CTA */}
        <div>
          <span
            className="
              mb-4 inline-flex rounded-full border border-primary/40 bg-primary/10
              px-4 py-1.5 font-customFont1 text-xs font-extrabold uppercase
              tracking-[0.14em] !text-[#111827] shadow-sm
              dark:!text-white
            "
          >
            {eyebrow}
          </span>

          <h2
            className="
              font-customFont2 text-4xl font-bold uppercase leading-none
              !text-[#111827]
              dark:!text-white
              sm:text-5xl lg:text-6xl
            "
          >
            {title}
          </h2>

          <div
            className="
              mt-6 space-y-5 font-customFont1 text-lg leading-relaxed
              !text-[#374151]
              dark:!text-[#D1D5DB]
              xl:text-xl
              [&_strong]:font-extrabold [&_strong]:!text-[#111827]
              dark:[&_strong]:!text-white
            "
          >
            {children}
          </div>

          {ctaLabel && (
            <div className="pt-8">
              <ResponsiveButton
                onClick={ctaAction}
                variant="hero"
                className="rounded-full px-8 py-3 text-lg"
              >
                <span className="inline-flex items-center gap-2">
                  {ctaLabel}
                  <MdArrowForward />
                </span>
              </ResponsiveButton>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

ServiceSection.propTypes = {
  id: PropTypes.string.isRequired,
  eyebrow: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  image: PropTypes.string.isRequired,
  imageAlt: PropTypes.string.isRequired,
  reverse: PropTypes.bool,
  children: PropTypes.node.isRequired,
  ctaLabel: PropTypes.string,
  ctaAction: PropTypes.func,
};

/* =====================================================
   PÄÄSIVU
===================================================== */
const DetailedServices = () => {
  const { hash } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    AOS.init({ duration: 900, once: true });

    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      }
    }
  }, [hash]);

  return (
    <>
      <Helmet>
        <title>Tapahtumatekniikan palvelut | Spiderlegs Productions</title>
        <meta
          name="description"
          content="Spiderlegs Productions tarjoaa tapahtumatekniikan palveluita: äänentoistoa, valaistusta, kuljetusta, asennusta ja teknistä toteutusta vuokrakalustolle."
        />
      </Helmet>

      <main
        className="
          relative w-full overflow-hidden pt-24 pb-20
          bg-gradient-to-br from-white via-[#F3FFFC] to-[#E9FBF7]
          text-[#111827]
          dark:from-[#050505] dark:via-[#08080A] dark:to-[#0D0D12]
          dark:text-white
        "
      >
        {/* Taustakoristeet */}
        <div
          aria-hidden="true"
          className="
            pointer-events-none absolute left-1/2 top-10 h-72 w-72
            -translate-x-1/2 rounded-full bg-primary/15 blur-3xl
            dark:bg-primary/10
          "
        />

        <div
          aria-hidden="true"
          className="
            pointer-events-none absolute -right-24 top-96 h-96 w-96
            rounded-full bg-[#30f2cf]/10 blur-3xl
            dark:bg-primary/10
          "
        />

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* HERO */}
          <section
            className="
              overflow-hidden rounded-[2rem] border border-[#DDE7E5]
              bg-white/90 p-6 shadow-xl backdrop-blur-xl
              dark:border-darkborder dark:bg-[#111114]/95
              sm:p-8 lg:p-10
            "
          >
            {/* Takaisin */}
            <ResponsiveButton
              onClick={() => {
                trackCTA("cta_takaisin", "palvelut");
                navigate(-1);
              }}
              variant="backbutton"
            >
              <div className="flex items-center gap-2">
                <MdArrowBack />
                Takaisin
              </div>
            </ResponsiveButton>

            <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:items-end">
              <div>
                <span
                  className="
                    mb-5 inline-flex rounded-full border border-primary/40 bg-primary/10
                    px-4 py-1.5 font-customFont1 text-xs font-extrabold uppercase
                    tracking-[0.14em] !text-[#111827] shadow-sm
                    dark:!text-white
                  "
                  data-aos="fade-in"
                  data-aos-duration="1200"
                >
                  Palvelut
                </span>

                <h1
                  className="
                    font-customFont2 text-5xl font-bold uppercase leading-none
                    !text-[#111827]
                    dark:!text-white
                    sm:text-6xl lg:text-7xl
                  "
                  data-aos="fade-in"
                  data-aos-duration="1500"
                >
                  Tapahtumatekniikan palvelut
                </h1>

                <p
                  className="
                    mt-6 max-w-4xl font-customFont1 text-lg leading-relaxed
                    !text-[#374151]
                    dark:!text-[#D1D5DB]
                    sm:text-xl
                  "
                  data-aos="fade-up"
                  data-aos-duration="1700"
                >
                  Spiderlegs Productions tarjoaa teknisiä palveluita
                  vuokrattavan tapahtumatekniikan ympärille. Palveluihin
                  kuuluvat esimerkiksi äänentoisto, valaistus, kuljetus,
                  asennus, käytön opastus ja tarvittaessa koko illan tekninen
                  toteutus.
                </p>

                <p
                  className="
                    mt-5 max-w-4xl font-customFont1 text-lg leading-relaxed
                    !text-[#374151]
                    dark:!text-[#D1D5DB]
                    sm:text-xl
                  "
                >
                  Tavoitteena on tehdä tapahtumatekniikan vuokraamisesta ja
                  käytöstä mahdollisimman helppoa: toimitamme, asennamme ja
                  opastamme sovitun palvelun mukaan, jotta vuokrakalusto toimii
                  luotettavasti paikan päällä.
                </p>
              </div>

              <aside
                className="
                  rounded-3xl border border-primary/30 bg-primary/10 p-5 shadow-sm
                  dark:border-primary/30 dark:bg-primary/5 dark:shadow-none
                "
              >
                <p
                  className="
                    font-customFont1 text-sm font-extrabold uppercase tracking-[0.12em]
                    !text-[#111827]
                    dark:!text-white
                  "
                >
                  Siirry suoraan palveluun
                </p>

                <div className="mt-4 flex flex-wrap gap-3">
                  {serviceQuickLinks.map((item) => (
                    <a
                      key={item.href}
                      href={item.href}
                      onClick={() =>
                        trackCTA("palvelu_pikalinkki_klikattu", item.label)
                      }
                      className="
                        rounded-full border border-[#DDE7E5] bg-white px-4 py-2
                        font-customFont1 text-sm font-bold
                        !text-[#111827] shadow-sm transition
                        hover:-translate-y-0.5 hover:border-primary hover:!text-primary hover:shadow-md
                        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary
                        focus-visible:ring-offset-2 focus-visible:ring-offset-white
                        dark:border-darkborder dark:bg-darksurface dark:!text-white
                        dark:hover:border-primary dark:hover:!text-primary
                        dark:focus-visible:ring-offset-[#111114]
                      "
                    >
                      {item.label}
                    </a>
                  ))}
                </div>
              </aside>
            </div>
          </section>

          <div className="mt-12 space-y-12">
            {/* 1. Tekniikka esiintyjille */}
            <ServiceSection
              id="tekniikka-esiintyjille"
              eyebrow="Esiintyjille ja bändeille"
              title="Tekniikka esiintyjille"
              image={artistTechImage}
              imageAlt="PA ja monitorointi esiintyjille"
              reverse
              ctaLabel="Varaa tekniikka keikalle"
              ctaAction={() => {
                trackCTA("cta_tekniikka_esiintyjille", "esiintyjat");
                navigate("/tee-varaus");
              }}
            >
              <p>
                Tarjoamme esiintyjille ja bändeille valmiin, keikkakäyttöön
                suunnitellun teknisen kokonaisuuden, joka sopii esimerkiksi
                klubeille, juhlatiloihin ja yritystilaisuuksiin.
              </p>

              <p>
                Äänentoiston sydämenä toimii
                <strong> PreSonus StudioLive 16.0.2 -digitaalimikseri</strong>,
                joka tarjoaa 16 kanavaa, 4 AUX-lähtöä monitorointiin sekä
                erittäin selkeän ja kirkkaan äänenlaadun. Kokonaisuuteen
                sisältyy <strong> ammattitaitoinen miksaaja</strong>, joka
                vastaa sovitun palvelun mukaan äänen ja valaistuksen teknisestä
                ajosta.
              </p>

              <p>
                Tarvittaessa kokonaisuuteen voidaan lisätä
                <strong> kaukokaapeli</strong> ja jopa
                <strong>
                  {" "}
                  4 kpl dB Technologies Flexsys FM12 (600 W)
                  -monitorikaiuttimia
                </strong>
                , jotta lavamonitorointi saadaan toimimaan esiintyjän tarpeiden
                mukaan.
              </p>

              <p>
                Valaistus- ja efektipuoli kattaa tunnelman ja lavailmeen:
                kokonaisuuteen sisältyy
                <strong> ADJ Stinger II -multiefektivalo</strong>,
                <strong> ADJ VF Volcano -savukone</strong> sekä
                <strong> 6 kpl Chauvet 56-24C LED -valoja</strong>. Savukone on
                pystysuoraan ampuva malli RGB-LEDeillä, joka tuo näyttävyyttä
                erityisesti tanssilattialle ja lavalle.
              </p>

              <p>
                Valoja ohjataan
                <strong> ADJ myDMX GO -langattomalla DMX-ohjaimella</strong>,
                ja liikkuvana tehosteena käytössä on
                <strong> Eliminator Lighting Stinger Spot 30</strong>, kompakti
                mutta tehokas 30 W moving head. Miksaaja huolehtii valaistuksen
                ajosta sovitun teknisen toteutuksen mukaisesti.
              </p>

              <p>
                Kokonaisuus räätälöidään tapahtuman ja esiintyjän teknisten
                tarpeiden mukaan. Esiintyjä voi keskittyä esitykseen, ja me
                huolehdimme sovitusta äänentoistosta, monitoroinnista, valosta
                ja teknisestä toimivuudesta.
              </p>
            </ServiceSection>

            {/* 2. Koko illan tekniikka */}
            <ServiceSection
              id="valojaanen"
              eyebrow="Huoleton kokonaisuus"
              title="Koko illan tekninen toteutus"
              image={valoaaniImage}
              imageAlt="Valo- ja äänitekniikka tapahtumassa"
              ctaLabel="Kysy toteutuksesta"
              ctaAction={() => {
                trackCTA("cta_koko_illan_tekniikka", "valo_aani");
                navigate("/tee-varaus");
              }}
            >
              <p>
                Koko illan tekninen toteutus tarkoittaa, että vastaamme
                tapahtuman äänentoistosta ja valaistuksesta sovitun teknisen
                kokonaisuuden osalta – suunnittelusta purkuun asti. Kartoitamme
                etukäteen asiakkaan tarpeet, tilaisuuden luonteen sekä tilan
                tekniset mahdollisuudet, jotta toteutus toimii sujuvasti.
              </p>

              <p>
                Toimitamme tarvittavat laitteet tapahtumapaikalle sovitun
                aikataulun mukaisesti, asennamme ne käyttövalmiiksi ja
                huolehdimme siitä, että tekniikka toimii luotettavasti sovitun
                palvelun ajan. Tapahtuman päätyttyä puramme kaluston ja viemme
                laitteet pois sovitusti.
              </p>

              <p>
                Valaistuksella ja tehosteilla voidaan rakentaa tilaan sopiva
                tunnelma:
                <strong> tanssilattia voidaan valaista erikseen</strong>{" "}
                liikkuvilla valoilla, LED-valoilla ja{" "}
                <strong>savukoneella</strong>. Sama tekninen kokonaisuus
                palvelee myös live-esiintymisiä, ohjelmanumeroita ja
                puheosuuksia, joissa valaistus ja ääni tukevat esitystä ja
                yleisökokemusta.
              </p>

              <p>
                Palvelu soveltuu erinomaisesti myös
                <strong> karaoke- ja Singa-toteutuksiin</strong>. Käytössämme
                on tarvittaessa suurempi äänentoistojärjestelmä, joka riittää
                jopa <strong> noin 600 hengen tiloihin</strong>, puheisiin,
                musiikkiin ja yhteislauluun selkeällä ja miellyttävällä
                äänenlaadulla.
              </p>

              <p>
                Ammattitaitoinen miksaaja huolehtii äänen ja valaistuksen
                toiminnasta sovitun teknisen toteutuksen aikana, reagoi
                tilanteisiin tarpeen mukaan ja varmistaa, että tekniikka tukee
                tilaisuuden etenemistä. Tämä on huoleton ratkaisu silloin, kun
                haluat varmuuden siitä, että ääni, valo ja kalusto toimivat
                paikan päällä.
              </p>
            </ServiceSection>

            {/* 3. Asennus */}
            <ServiceSection
              id="opastuspalvelu"
              eyebrow="Toimitus käyttövalmiiksi"
              title="Asennus- ja opastuspalvelu"
              image={opastusImage}
              imageAlt="Äänentoiston asennus"
              reverse
              ctaLabel="Pyydä tarjous"
              ctaAction={() => {
                trackCTA("cta_tarjouspyynto", "asennus_opastus");
                navigate("/tee-varaus");
              }}
            >
              <p>
                Mikäli asiakas tilaa ainoastaan äänentoiston ja haluaa hoitaa
                laitteiden käytön itse, toimitamme kaluston tapahtumapaikalle
                sovitun aikataulun mukaisesti ja asennamme sen valmiiksi
                käyttökuntoon.
              </p>

              <p>
                Käymme laitteiston toiminnan rauhassa läpi paikan päällä ja
                annamme selkeät, käytännönläheiset käyttöohjeet, jotta
                äänentoiston käyttäminen on helppoa ja turvallista myös ilman
                aiempaa kokemusta.
              </p>

              <p>
                Tämä{" "}
                <strong>
                  asennus ja opastus sisältyy toimitukseen ilman lisämaksua
                </strong>
                , kun äänentoisto toimitetaan kohteeseen. Asiakas vastaa itse
                laitteiden käytöstä tapahtuman aikana, ja me huolehdimme siitä,
                että kaikki on oikein kytketty ja valmiina käyttöön ennen
                tilaisuuden alkua.
              </p>

              <p>
                Palvelu sopii erityisesti juhliin, tilaisuuksiin ja
                tapahtumiin, joissa halutaan luotettava äänentoisto ilman
                erillistä tekniikanhoitajaa – mutta kuitenkin varmuus siitä,
                että laitteisto toimii kuten pitää.
              </p>
            </ServiceSection>

            {/* 4. Kuljetus */}
            <ServiceSection
              id="perakarryn-vuokraus"
              eyebrow="Kuljetus ja erillinen vuokraus"
              title="Kuljetuspalvelu & peräkärryn vuokraus"
              image={transportImage}
              imageAlt="Peräkärry ja kuljetuskalusto"
              ctaLabel="Varaa nyt"
              ctaAction={() => {
                trackCTA("cta_vuokrattavat", "kuljetuspalvelu");
                navigate("/tuote/hirvi-350-kuomukarry");
              }}
            >
              <p>
                Kun asiakas tilaa esimerkiksi
                <strong> koko illan teknisen toteutuksen</strong> tai
                <strong> tekniikan esiintyjille</strong>, laitteisto kuljetetaan
                tapahtumapaikalle turvallisesti ja aikataulussa osana palvelua.
              </p>

              <p>
                Jos vuokrauskori sisältää
                <strong> enemmän kuin yhden PA-paketin</strong> tai
                <strong> yhden PA-paketin ja monitorit</strong>, kalusto ei
                mahdu turvallisesti henkilöautoon. Tällöin käytämme peräkärryä
                toimitukseen, ja sen hinta on
                <strong> 20 € / suunta</strong> osana kuljetuspalvelua.
              </p>

              <p>
                Kuljetuksissa käytämme <strong>HIRVI 350 -kuomukärryä</strong>,
                joka soveltuu hyvin erilaisten laitteiden ja tavaroiden
                kuljettamiseen. Kuljetus veloitetaan erikseen
                <strong> voimassa olevien kilometrikorvausten mukaan</strong>.
              </p>

              <p>
                Sama kuomukärry on mahdollista
                <strong> vuokrata myös erikseen omaan käyttöön</strong>.
                Peräkärryn vuokraus tapahtuu Vuokrattavat-osiosta, ja kärryllä
                on oma varauskalenteri, josta valitaan nouto- ja
                palautusajankohta.
              </p>

              <p>
                <strong>Vuokra-aika</strong> alkaa siitä kellonajasta ja
                päivästä, jolloin kärry noudetaan, ja päättyy siihen hetkeen,
                jolloin se palautetaan.
                <strong> Vuokrahinta on 25 € / vuorokausi (24 h).</strong>
              </p>

              <p>
                Nouto ja palautus tapahtuvat <strong>Keravan Saviolla</strong>.
                Tarkemmat ohjeet toimitetaan varauksen vahvistuksen jälkeen.
              </p>

              <p>
                Peräkärryä tulee käyttää vain tavanomaiseen kuljetukseen,
                huolellisesti ja liikennesääntöjä noudattaen. Kärry tulee
                palauttaa{" "}
                <strong>
                  sovittuna ajankohtana, puhdistettuna ja samassa kunnossa kuin
                  se oli noudettaessa
                </strong>
                . Vuokra-ajan ylityksestä veloitetaan
                <strong> kaksinkertainen vuorokausihinta</strong>.
              </p>

              <p>
                Kärryä ei saa käyttää maa-ainesten, kuten soran, hiekan tai
                mullan, kuljettamiseen. Vuokraaja vastaa kuorman sitomisesta,
                painorajoista sekä siitä, että vetoauto ja ajokorttiluokka
                soveltuvat peräkärryn vetämiseen.
              </p>

              <p>
                Peräkärry on vakuutettu, ja
                <strong> omavastuu vahinkotapauksissa on 200 €</strong>.
                Vuokraaja vastaa vuokra-aikana aiheutuneista vahingoista,
                puhdistustarpeista ja mahdollisista sakoista sekä aina
                kuljettamastaan kuormasta.
              </p>

              <p>Varaamalla ja käyttämällä palvelua hyväksyt nämä ehdot.</p>
            </ServiceSection>
          </div>
        </div>
      </main>
    </>
  );
};

export default DetailedServices;